import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { Toast as ToastType, useToast } from '@/contexts/ToastContext';

interface ToastProps {
  toast: ToastType;
}

export function Toast({ toast }: ToastProps) {
  const { hideToast } = useToast();

  const config = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-chart-4/10',
      borderColor: 'border-chart-4/30',
      iconColor: 'text-chart-4',
      titleColor: 'text-chart-4',
      messageColor: 'text-chart-4',
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-destructive/10',
      borderColor: 'border-destructive/30',
      iconColor: 'text-destructive',
      titleColor: 'text-destructive',
      messageColor: 'text-destructive',
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-secondary/10',
      borderColor: 'border-secondary/30',
      iconColor: 'text-secondary-foreground',
      titleColor: 'text-secondary-foreground',
      messageColor: 'text-secondary-foreground',
    },
    info: {
      icon: Info,
      bgColor: 'bg-accent/10',
      borderColor: 'border-accent',
      iconColor: 'text-primary',
      titleColor: 'text-foreground',
      messageColor: 'text-accent',
    },
  };

  const style = config[toast.type];
  const Icon = style.icon;

  return (
    <div
      className={`
        ${style.bgColor} ${style.borderColor}
        border rounded-lg shadow-lg hover:shadow-xl p-4
        flex items-start gap-3 min-w-[360px] max-w-md
        backdrop-blur-sm bg-opacity-95
        transform transition-all duration-300 ease-out
        animate-slide-in-right
      `}
      role="alert"
      style={{
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      <div className={`${style.bgColor} ${style.borderColor} border rounded-full p-1.5 flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${style.iconColor}`} />
      </div>

      <div className="flex-1 min-w-0">
        <h4 className={`font-semibold text-sm ${style.titleColor}`}>{toast.title}</h4>
        {toast.message && (
          <p className={`text-sm mt-1 ${style.messageColor} leading-relaxed`}>{toast.message}</p>
        )}
      </div>

      <button
        onClick={() => hideToast(toast.id)}
        className={`
          ${style.iconColor} hover:opacity-70
          flex-shrink-0 transition-all rounded-full hover:bg-black/5 p-1
          hover:rotate-90
        `}
        aria-label="Fechar"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { toasts } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col items-end pointer-events-none">
      <div className="pointer-events-auto space-y-3">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} />
        ))}
      </div>
    </div>
  );
}
